import React from 'react'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'
import {
  RETRY_CONFIG_NO_SCHEDULE_LABEL,
  retryActionTypeTitles,
} from 'src/constants/scheduler/constants'
import type { RetryActionType } from 'src/constants/scheduler/enums'
import type { RetryConfigListRow } from 'src/interfaces/retryConfig'
import { formatDateTime, parseFlexibleDateTime } from 'src/utils/grid'

/**
 * The list left-joins only `scheduled` jobs, so a null `scheduledAt` means nothing is pending —
 * not that the data is missing. `RETRY_CONFIG_NO_SCHEDULE_LABEL` ("Completed") renders that real
 * state; it is not a placeholder for missing data.
 */
const renderScheduledAt = (params: GridRenderCellParams<RetryConfigListRow, Date | null>) => {
  if (!params.value) {
    return <span>{RETRY_CONFIG_NO_SCHEDULE_LABEL}</span>
  }

  return <span>{formatDateTime(params.value)}</span>
}

/**
 * Only `scheduledAt` is sortable — the backend's `@SortQuery` whitelist (`RetryConfigsFieldsToSort`)
 * has exactly that one member, and anything else is rejected. Sorting itself is server-side
 * (`ScheduledActionsTable` sets `sortingMode="server"`), so this flag only controls whether the
 * column header offers the control.
 */
export const scheduledActionColumns: Array<GridColDef<RetryConfigListRow>> = [
  { field: 'id', headerName: 'ID', flex: 1, sortable: false },
  { field: 'relationId', headerName: 'Relation ID', flex: 1, sortable: false },
  { field: 'partnerDriverId', headerName: 'Partner driver ID', flex: 1, sortable: false },
  { field: 'partnerMarket', headerName: 'Market', flex: 1, sortable: false },
  {
    field: 'actionType',
    headerName: 'Action type',
    flex: 1,
    sortable: false,
    valueGetter: (value: RetryActionType) => {
      return retryActionTypeTitles[value] ?? value
    },
  },
  { field: 'maxAttempts', headerName: 'Max attempts', width: 130, sortable: false },
  { field: 'backoffMultiplier', headerName: 'Backoff multiplier', width: 160, sortable: false },
  { field: 'timeoutMinutes', headerName: 'Timeout (min)', width: 130, sortable: false },
  { field: 'currentAttempt', headerName: 'Current attempt', width: 140, sortable: false },
  {
    field: 'disabled',
    headerName: 'Disabled',
    width: 110,
    sortable: false,
    valueGetter: (value: boolean) => {
      return value ? 'Yes' : 'No'
    },
  },
  {
    field: 'scheduledAt',
    headerName: 'Scheduled at',
    flex: 1,
    sortable: true,
    // Pair `parseFlexibleDateTime` / `formatDateTime` — the repo's existing grid-date utils
    // (src/utils/grid.ts) — rather than parsing the ISO string ad hoc; `scheduledAt` carries a
    // time of day, unlike `parseIsoDate`, which deliberately flattens to local midnight.
    valueGetter: parseFlexibleDateTime,
    renderCell: renderScheduledAt,
  },
]
